import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { apiGet, apiPost, apiDelete } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Menu, X, Home, Radar, Network, Zap, List, BarChart3, History, Settings, LogOut, AlertTriangle, Globe, Shield, DollarSign, Activity } from "lucide-react";
import { RiskStatusStrip } from "@/pages/RiskLabPage";

const NAV_ITEMS = [
  { to: "/oracle-trades", label: "Oracle Trades", icon: Home },
  { to: "/risk-lab", label: "Risk Lab", icon: Activity },
  { to: "/oracle-trades/tv", label: "TV Mode", icon: BarChart3 },
  { to: "/stocks", label: "CSE Market", icon: Globe },
  { to: "/us-stocks", label: "US Stocks", icon: BarChart3 },
  { to: "/portfolio", label: "Portfolio", icon: Shield },
  { to: "/admin/orb", label: "Admin ORB", icon: Settings },
  { to: "/settings", label: "API Keys", icon: Zap },
];

/** Oracle Trades HUD navigation (admin-only) */
export function OracleNav({ onLogout }) {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* Desktop top bar */}
      <div className="hidden md:flex items-center gap-2 flex-wrap">
        {NAV_ITEMS.map((n) => {
          const Icon = n.icon;
          const active = loc.pathname === n.to || (n.to === "/admin/orb" && loc.pathname.startsWith("/admin/orb"));
          return (
            <Link key={n.to} to={n.to} className={`ora-navlink ${active ? "active" : ""}`} data-testid={`oracle-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <Icon size={14} /> {n.label}
            </Link>
          );
        })}
        <button onClick={onLogout} className="ora-navlink" data-testid="oracle-nav-logout">
          <LogOut size={14} /> Logout
        </button>
      </div>
      {/* Mobile hamburger */}
      <div className="md:hidden">
        <button className="ora-navlink" onClick={() => setOpen(!open)} data-testid="oracle-nav-mobile-btn">
          {open ? <X size={16} /> : <Menu size={16} />}
        </button>
        {open && (
          <div className="fixed inset-0 bg-black/80 z-50 p-6 flex flex-col gap-3" onClick={() => setOpen(false)}>
            {NAV_ITEMS.map((n) => {
              const Icon = n.icon;
              return (
                <Link key={n.to} to={n.to} className="ora-navlink" onClick={() => setOpen(false)}>
                  <Icon size={16} /> {n.label}
                </Link>
              );
            })}
            <button onClick={onLogout} className="ora-navlink"><LogOut size={16} /> Logout</button>
          </div>
        )}
      </div>
    </>
  );
}

// --- Helpers ---
const fmt = (v, d = 2) => (v == null || isNaN(v) ? "—" : Number(v).toFixed(d));
const fmtInt = (v) => (v == null ? "—" : Math.round(v).toString());
const fmtPct = (v, d = 1) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(d)}%`);
const fmtR = (v) => (v == null || isNaN(v) ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}R`);
const fmtDate = (iso) => (iso ? iso.slice(0, 10) : "—");
const fmtMoney = (v) => (v == null || isNaN(v) ? "—" :
  `${v < 0 ? "-" : ""}$${Math.abs(Number(v)).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
const fmtSignedMoney = (v) => (v == null || isNaN(v) ? "—" :
  `${v >= 0 ? "+" : "-"}$${Math.abs(Number(v)).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);

/* -------------------------------------------------------------------------
 * useLivePrices — Part C
 * -------------------------------------------------------------------------
 * Batched Alpaca IEX latest-trade polling with per-trade P&L enrichment.
 *
 * Cadence:
 *   • Market open  → poll every 60s
 *   • Just closed  → one trailing poll ~2min after the close, then idle
 *   • Market shut  → do not poll
 *   • Tab hidden   → pause the timer
 *
 * All open positions on the page share ONE request. Server-side the
 * endpoint caches for 60s so multiple tabs cost at most one Alpaca call
 * per minute.
 * ------------------------------------------------------------------------- */
function useLivePrices(openTrades, { isMarketOpen, extraSymbols = [] }) {
  const [pricesBySymbol, setPrices] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);
  const [stale, setStale] = useState(false);
  const [session, setSession] = useState("regular"); // "regular" | "pre" | "post" | "closed"

  // Memoised deduped symbol list so the fetch effect does not run every
  // render just because the array reference changed. We include both the
  // open-position symbols AND any extra symbols (e.g. Today's Signals that
  // have not yet become open positions) so the UI can show a LIVE price
  // on signal cards too.
  const symKey = useMemo(() => {
    const set = new Set();
    for (const t of openTrades || []) {
      if (t?.symbol) set.add(String(t.symbol).toUpperCase());
    }
    for (const s of extraSymbols || []) {
      if (s) set.add(String(s).toUpperCase());
    }
    return [...set].sort().join(",");
  }, [openTrades, extraSymbols]);

  useEffect(() => {
    if (!symKey) { setPrices({}); return; }
    let timer, cancelled = false;
    const fetchOnce = async (force = false) => {
      try {
        const url = force
          ? `/orb/live-prices?symbols=${encodeURIComponent(symKey)}&force=1&_t=${Date.now()}`
          : `/orb/live-prices?symbols=${encodeURIComponent(symKey)}`;
        const r = await apiGet(url);
        if (cancelled) return;
        setPrices((prev) => ({ ...prev, ...(r.prices || {}) }));
        if (r.session) setSession(r.session);
        setLastUpdate(new Date());
        setStale(false);
      } catch (_) {
        if (!cancelled) setStale(true);
      }
    };
    const schedule = () => {
      if (cancelled) return;
      if (document.hidden) { timer = setTimeout(schedule, 15000); return; }
      fetchOnce();
      // 60s while live; 15min window if market just closed to catch the
      // official last print, then idle. When fully closed, stop entirely.
      const interval = isMarketOpen ? 60_000 : 15 * 60_000;
      if (isMarketOpen || !lastUpdate) {
        timer = setTimeout(schedule, interval);
      }
    };
    schedule();
    const onVis = () => { clearTimeout(timer); schedule(); };
    document.addEventListener("visibilitychange", onVis);
    // Expose a manual-refresh handler via a window event so UI buttons
    // anywhere on the page can trigger a force-fetch without prop drilling.
    const onForce = () => fetchOnce(true);
    window.addEventListener("orb:refresh-prices", onForce);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("orb:refresh-prices", onForce);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symKey, isMarketOpen]);

  // Enrich each open trade with live P&L / R / bar position. All computations
  // are DISPLAY-ONLY: no server state changes.
  // Price lookup is DEFENSIVE — handles the case where the stored symbol
  // has whitespace, exchange prefix, or unexpected casing. We also DECOUPLE
  // "has price" from "has P&L" so a trade with a valid price but missing
  // entry/shares still shows the live quote instead of a blank card.
  const enriched = useMemo(() => {
    // Build a lookup table keyed by every variant of every price key so
    // t.symbol can match "NET", "net", " NET ", or "NASDAQ:NET".
    const pxLookup = {};
    for (const k of Object.keys(pricesBySymbol || {})) {
      const clean = String(k).trim();
      pxLookup[clean] = pricesBySymbol[k];
      pxLookup[clean.toUpperCase()] = pricesBySymbol[k];
      pxLookup[clean.toLowerCase()] = pricesBySymbol[k];
    }
    const resolveSym = (raw) => {
      if (!raw) return null;
      const s = String(raw).trim();
      if (!s) return null;
      const tail = s.includes(":") ? s.split(":").pop() : s;
      return (
        pxLookup[s] ||
        pxLookup[s.toUpperCase()] ||
        pxLookup[s.toLowerCase()] ||
        pxLookup[tail] ||
        pxLookup[tail.toUpperCase()] ||
        pxLookup[tail.toLowerCase()] ||
        null
      );
    };

    const out = (openTrades || []).map((t) => {
      const rawSym = t?.symbol ?? t?.ticker ?? "";
      const price = resolveSym(rawSym);
      const entry = Number(t.entry) || 0;
      const stop  = Number(t.stop)  || 0;
      const target = Number(t.target) || 0;
      const shares = Number(t.shares) || 0;

      // No price at all → waiting.
      if (!price) {
        if (process.env.NODE_ENV !== "production") {
          // One-shot developer hint: shows why the card is blank.
          // eslint-disable-next-line no-console
          console.warn("[live-prices] no price for", rawSym,
                       "— keys available:", Object.keys(pricesBySymbol || {}));
        }
        return { ...t, last_price: null, last_time: null, last_source: null, stale: false };
      }

      const p = Number(price.price);
      if (!Number.isFinite(p) || p <= 0) {
        return { ...t, last_price: null, last_time: null, last_source: null, stale: !!price.stale };
      }

      // We HAVE a valid price. Always set last_price / last_time / source
      // so the card shows the live quote even if P&L cannot be computed.
      const base = {
        ...t,
        last_price: p,
        last_time:   price.time   || null,
        last_source: price.source || null,
        stale: !!price.stale,
      };

      // P&L / R / bar position — only when we have good sizing fields.
      if (entry > 0 && shares > 0 && stop > 0 && target > 0) {
        const pnlDollars = (p - entry) * shares;
        const pnlPct = entry > 0 ? (p / entry - 1) : 0;
        const risk = entry - stop;
        const pnlR = risk > 0 ? (p - entry) / risk : 0;
        const barPos = (() => {
          if (target === stop) return 0.5;
          const raw = (p - stop) / (target - stop);
          return Math.max(-0.08, Math.min(1.08, raw));
        })();
        return { ...base, pnl_dollars: pnlDollars, pnl_pct: pnlPct, pnl_r: pnlR, bar_pos: barPos };
      }
      return base;
    });
    return out;
  }, [openTrades, pricesBySymbol]);

  // Per-setup totals
  const totals = useMemo(() => {
    const z = () => ({ pnl: 0, cost: 0, count: 0 });
    const acc = { s5: z(), s6: z() };
    for (const t of enriched) {
      const k = t.setup === 6 ? "s6" : "s5";
      acc[k].count += 1;
      acc[k].cost += Number(t.entry || 0) * Number(t.shares || 0);
      if (t.pnl_dollars != null) acc[k].pnl += Number(t.pnl_dollars);
    }
    return acc;
  }, [enriched]);

  return { pricesBySymbol, enriched, totals, lastUpdate, stale, session };
}

/** Short "15:42 UK" from an ISO timestamp. */
function _fmtClockUK(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Europe/London" });
  } catch { return ""; }
}

/** Session chip — small amber "AFTER HRS" / cyan "PRE-MKT" label. */
function SessionChip({ session }) {
  if (!session || session === "regular") return null;
  const map = {
    pre:    { label: "PRE-MKT",  color: "#9fe7ff", bg: "rgba(59,217,255,.12)", bd: "rgba(59,217,255,.5)" },
    post:   { label: "AFTER HRS", color: "#ffc65c", bg: "rgba(255,198,92,.12)", bd: "rgba(255,198,92,.55)" },
    closed: { label: "MARKET CLOSED", color: "#8ea6c6", bg: "rgba(142,166,198,.1)", bd: "rgba(142,166,198,.4)" },
  };
  const c = map[session] || map.closed;
  return (
    <span data-testid={`session-chip-${session}`}
          style={{
            display: "inline-flex", alignItems: "center",
            padding: "1px 6px", borderRadius: 999,
            fontSize: 10, letterSpacing: ".18em", fontWeight: 700,
            color: c.color, background: c.bg, border: `1px solid ${c.bd}`,
          }}>
      {c.label}
    </span>
  );
}

/** Price source chip — tiny inline tag showing which provider delivered
 *  the current live price. Hidden when source is "trade" (the common
 *  Alpaca real-time path) to avoid noise, visible for every fallback. */
function PriceSourceChip({ source }) {
  if (!source || source === "trade") return null;
  const label = source === "finnhub"    ? "via FINNHUB"
              : source === "minute_bar" ? "min bar"
              : source === "daily_bar"  ? "prev close"
              : source === "snapshot"   ? "snapshot"
              :                           source;
  const color = source === "finnhub" ? "#c7a7ff" : "#9fe7ff";
  const bd    = source === "finnhub" ? "rgba(199,167,255,.5)" : "rgba(159,231,255,.5)";
  return (
    <span data-testid={`price-source-${source}`}
          style={{
            padding: "0 5px", borderRadius: 999,
            fontSize: 8, letterSpacing: ".18em", fontWeight: 700,
            color, border: `1px solid ${bd}`,
          }}>
      {label}
    </span>
  );
}

/** Session banner shown just under the Oracle header when outside RTH.
 *  Reinforces the rule that extended-hours prices never trigger exits. */
function SessionBanner({ session }) {
  if (!session || session === "regular") return null;
  const msgs = {
    pre:    "Pre-market session · prices shown are informational only. Exits never trigger before 09:30 NY.",
    post:   "After-hours session · prices shown are informational only. Exits never trigger after 16:00 NY.",
    closed: "Market closed · prices shown are the last regular-session close until the next US trading day.",
  };
  const color = session === "post" ? "#ffc65c"
              : session === "pre"  ? "#9fe7ff" : "#8ea6c6";
  const bg    = session === "post" ? "rgba(255,198,92,.08)"
              : session === "pre"  ? "rgba(59,217,255,.08)" : "rgba(142,166,198,.06)";
  const bd    = session === "post" ? "rgba(255,198,92,.45)"
              : session === "pre"  ? "rgba(59,217,255,.4)" : "rgba(142,166,198,.3)";
  return (
    <div data-testid="session-banner"
         className="mb-3 rounded flex items-center gap-2 px-3 py-2 text-xs font-mono"
         style={{ color, background: bg, border: `1px solid ${bd}` }}>
      <SessionChip session={session} />
      <span>{msgs[session]}</span>
    </div>
  );
}

/** Closed Trades Today — the exits log the user asked for.
 *  Reads /api/orb/closed-today and shows time (UK) · symbol · setup · reason
 *  · entry · exit · P&L $/R · correction note (if any). */
function ClosedTradesToday() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await apiGet("/orb/closed-today");
      setRows(r.rows || []);
      setErr("");
    } catch (e) {
      setErr(e?.message || "Failed to load exits log");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 60_000);
    const onForce = () => load();
    window.addEventListener("orb:refresh-closed", onForce);
    return () => { clearInterval(t); window.removeEventListener("orb:refresh-closed", onForce); };
  }, [load]);

  const refreshBtn = (
    <button
      type="button"
      data-testid="closed-today-refresh"
      onClick={() => { load(); toast.success("Reloading exits log…"); }}
      className="text-[10px] rounded border px-2 py-[2px] uppercase tracking-widest font-mono hover:bg-cyan-400/10"
      style={{ borderColor: "rgba(59,217,255,.45)", color: "#9fe7ff" }}
    >
      ↻ Reload
    </button>
  );

  return (
    <Panel title={`Closed Trades Today · ${rows.length}`} id="closed-today"
           data-testid="oracle-closed-today" right={refreshBtn}>
      {loading && <div className="ora-sub">Loading…</div>}
      {err && !loading && <div className="text-red-400 text-sm">{err}</div>}
      {!loading && !err && rows.length === 0 && (
        <div className="ora-sub">No trades closed yet today.</div>
      )}
      {!loading && !err && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono" data-testid="exits-table">
            <thead>
              <tr className="text-left opacity-60">
                <th className="pr-3 py-1">Time (UK)</th>
                <th className="pr-3 py-1">Symbol</th>
                <th className="pr-3 py-1">Setup</th>
                <th className="pr-3 py-1">Reason</th>
                <th className="pr-3 py-1 text-right">Entry</th>
                <th className="pr-3 py-1 text-right">Exit</th>
                <th className="pr-3 py-1 text-right">P&amp;L $</th>
                <th className="pr-3 py-1 text-right">R</th>
                <th className="pr-3 py-1">Feed / Note</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pnlCol = r.pnl_dollars >= 0 ? "text-emerald-300" : "text-red-300";
                const reasonCol =
                    r.exit_reason === "Target"   ? "text-emerald-300"
                  : r.exit_reason === "Stop"     ? "text-red-300"
                  : r.exit_reason === "Max hold" ? "text-amber-300"
                  :                                "text-cyan-200";
                return (
                  <tr key={r.id} className="border-t border-cyan-400/10">
                    <td className="pr-3 py-1 whitespace-nowrap">{_fmtClockUK(r.exit_time)}</td>
                    <td className="pr-3 py-1 font-bold">{r.symbol}</td>
                    <td className="pr-3 py-1 text-cyan-200">S{r.setup}</td>
                    <td className={`pr-3 py-1 ${reasonCol}`}>
                      {r.exit_reason}{r.gap_fill && <span className="opacity-60 ml-1">(gap)</span>}
                    </td>
                    <td className="pr-3 py-1 text-right">{r.entry != null ? Number(r.entry).toFixed(2) : "—"}</td>
                    <td className="pr-3 py-1 text-right">{r.exit  != null ? Number(r.exit ).toFixed(2) : "—"}</td>
                    <td className={`pr-3 py-1 text-right ${pnlCol}`}>
                      {r.pnl_dollars != null
                        ? `${r.pnl_dollars >= 0 ? "+" : "-"}$${Math.abs(Number(r.pnl_dollars)).toLocaleString("en-US", { maximumFractionDigits: 0 })}`
                        : "—"}
                    </td>
                    <td className={`pr-3 py-1 text-right ${pnlCol}`}>
                      {r.result_r != null ? `${r.result_r >= 0 ? "+" : ""}${Number(r.result_r).toFixed(2)}R` : "—"}
                    </td>
                    <td className="pr-3 py-1 text-[10px] opacity-70">
                      {r.correction_feed && <span className="text-amber-300">SIP · </span>}
                      {r.correction_note || ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

const countdown = (sec) => {
  if (!sec) return "—";
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return `${h}h ${m}m ${String(s).padStart(2, "0")}s`;
};

// --- Header + sections ---
function Header({ data }) {
  const m = data.market;
  const sch = data.scheduler;
  const tg = data.telegram;
  return (
    <header className="ora-panel ora-floaty p-4 mb-5 flex flex-wrap items-center gap-4 justify-between">
      <div className="flex items-center gap-3">
        <svg width="52" height="52" viewBox="0 0 52 52" className="ora-spin">
          <circle cx="26" cy="26" r="24" fill="none" stroke="rgba(59,217,255,.35)" strokeWidth="1.5" />
          <circle cx="26" cy="26" r="19" fill="none" stroke="#3bd9ff" strokeWidth="2" strokeDasharray="10 6" />
          <circle cx="26" cy="26" r="11" fill="#3bd9ff22" stroke="#3bd9ff" strokeWidth="1.5" />
          <circle cx="26" cy="26" r="5" fill="#e9fbff" className="ora-pulse" />
        </svg>
        <div>
          <div className="ora-h" style={{ fontSize: 22 }}>ORACLE TRADES</div>
          <div className="ora-sub">CSE Investor · US opening-range breakout · Setup 5 / Setup 6</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className={`flex items-center gap-2 px-3 py-1.5 border rounded ${m.is_open ? "border-green-400/50" : "border-amber-400/50"}`}>
          <span className={`ora-pulse inline-block w-2.5 h-2.5 rounded-full ${m.is_open ? "bg-green-400" : "bg-amber-400"}`} />
          <span className="font-mono text-xs font-bold">{m.is_open ? "MARKET OPEN" : "MARKET CLOSED"}</span>
          <span className="text-[11px] opacity-70">· {m.countdown_label} {countdown(m.countdown_sec)}</span>
        </div>
        <div className="font-mono text-xs leading-tight text-right">
          <div>{m.ny_date}</div>
          <div className="opacity-70">NY {m.ny_time} · UK {m.uk_time}</div>
        </div>
        <div className="font-mono text-[11px] leading-tight">
          <div>Leader: {sch.is_leader ? <span className="text-green-400">YES</span> : <span className="opacity-60">no</span>}</div>
          <div><TelegramStatus tg={tg} /></div>
          <div>Log: {data.log_period?.name || "—"}</div>
        </div>
      </div>
    </header>
  );
}


/** Header Telegram chip. Reads data.telegram (set by the dashboard) with
 * three outcomes:
 *   • status "on"      → green "Telegram: on" + "last sent HH:MM UK" title
 *   • status "error"   → red "Telegram: error" + last error message title
 *   • status "not_set" → grey "Telegram: not set"
 *
 * The dashboard builds the status strictly from our own sendMessage
 * history + the presence of the two secrets. Never calls getUpdates. */
function TelegramStatus({ tg }) {
  const status = tg?.status || (tg?.configured ? "on" : "not_set");
  const lastSentIso = tg?.last_sent_at;
  const lastSentUk = (() => {
    if (!lastSentIso) return "nothing sent yet";
    try {
      const d = new Date(lastSentIso);
      return d.toLocaleString("en-GB", {
        timeZone: "Europe/London",
        hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short",
      });
    } catch { return lastSentIso; }
  })();
  const label = status === "on" ? "on" : status === "error" ? "error" : "not set";
  const cls   = status === "on" ? "text-green-400"
              : status === "error" ? "text-red-400"
              : "opacity-60";
  const title = status === "error"
    ? `Last send failed: ${tg?.last_error_message || "unknown"}\nAt: ${lastSentUk} UK`
    : status === "not_set"
      ? `Token set: ${tg?.token_set ? "yes" : "no"} · Chat ID set: ${tg?.chat_id_set ? "yes" : "no"}`
      : `Last sent: ${lastSentUk} UK`;
  return (
    <span data-testid="telegram-status" title={title}>
      Telegram: <span className={cls}>{label}</span>
    </span>
  );
}



function Panel({ title, right, children, id, "data-testid": testId }) {
  return (
    <section id={id} className="ora-panel ora-floaty p-4 mb-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="ora-h">{title}</div>
        {right}
      </div>
      {children}
    </section>
  );
}

function TodaysSignals({ data, pricesBySymbol, session }) {
  const sigs = data.todays_signals || [];
  const refreshBtn = (
    <div className="flex items-center gap-2">
      <SessionChip session={session} />
      <button
        type="button"
        data-testid="orb-refresh-prices-btn-signals"
        onClick={() => {
          window.dispatchEvent(new Event("orb:refresh-prices"));
          toast.success("Refreshing live prices…");
        }}
        className="text-[10px] rounded border px-2 py-[2px] uppercase tracking-widest font-mono hover:bg-cyan-400/10"
        style={{ borderColor: "rgba(59,217,255,.45)", color: "#9fe7ff" }}
        title="Force-fetch latest Alpaca prices now (bypasses 60s cache)"
      >
        ↻ Refresh prices
      </button>
    </div>
  );
  if (!sigs.length) return (
    <Panel title="Today's Signals" id="signals" data-testid="oracle-signals" right={refreshBtn}>
      <div className="ora-sub">No signals triggered today. Scanner is active during market hours.</div>
    </Panel>
  );
  return (
    <Panel title="Today's Signals" id="signals" data-testid="oracle-signals" right={refreshBtn}>
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
        {sigs.map((t, i) => {
          // Prefer backend-formatted bar-close times. If the trade doc
          // predates the live-engine upgrade, derive both zones from the
          // raw entry_time (bar OPEN) by adding 30 minutes and formatting
          // with `Intl.DateTimeFormat` — this handles DST correctly
          // (US clocks rolled back on 1 Nov 2026).
          let timeNy = t.entry_time_ny || "";
          let timeUk = t.entry_time_uk || "";
          if ((!timeNy || !timeUk) && t.entry_time) {
            try {
              const d = new Date(t.entry_time);
              // Bar OPEN → bar CLOSE
              d.setUTCMinutes(d.getUTCMinutes() + 30);
              if (!timeNy) {
                timeNy = d.toLocaleTimeString("en-GB", {
                  hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "America/New_York",
                });
              }
              if (!timeUk) {
                timeUk = d.toLocaleTimeString("en-GB", {
                  hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/London",
                });
              }
            } catch (_) { /* ignore */ }
          }
          // Risk $ fallback: compute shares × (entry − stop) client-side
          // when the backend didn't persist risk_dollars on the trade row.
          const riskD = (t.risk_dollars != null && t.risk_dollars !== 0)
            ? t.risk_dollars
            : (t.shares && t.entry != null && t.stop != null
                ? Math.max(0, (t.entry - t.stop) * t.shares)
                : null);
          return (
          <article key={i} className="relative overflow-hidden rounded-lg border p-3 flex flex-col gap-2"
            style={{ borderColor: t.earnings_warning ? "rgba(255,181,71,.6)" : "rgba(59,217,255,.35)" }}>
            <div className="ora-scan" />
            <div className="flex items-center justify-between">
              <div className="ora-h" style={{ fontSize: 20 }}>{t.symbol}</div>
              <div className="font-mono text-[11px] opacity-80 text-right whitespace-nowrap">
                {timeNy} NY{timeUk ? ` · ${timeUk} UK` : ""}
              </div>
            </div>
            <div className="flex gap-2">
              <span className="ora-chip ora-chip-cyan">SETUP {t.setup}</span>
              {t.earnings_warning && (
                <span className="ora-chip ora-chip-amber" title="Earnings in holding window">
                  <AlertTriangle size={10} style={{ display: "inline", marginRight: 2 }} /> EARNINGS
                </span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-1 text-[11px]">
              <div className="border border-cyan-400/30 rounded p-1.5">
                <div className="opacity-70">BUY</div><div className="font-mono text-sm">{fmt(t.entry)}</div>
              </div>
              <div className="border border-red-400/40 rounded p-1.5">
                <div className="opacity-70 text-red-300">STOP</div><div className="font-mono text-sm text-red-300">{fmt(t.stop)}</div>
              </div>
              <div className="border border-green-400/40 rounded p-1.5">
                <div className="opacity-70 text-green-300">TARGET</div><div className="font-mono text-sm text-green-300">{fmt(t.target)}</div>
              </div>
            </div>
            <div className="font-mono text-[11px] flex justify-between opacity-80">
              <span>{t.shares} shares</span>
              <span>risk ${riskD != null ? fmt(riskD) : "—"}</span>
            </div>
            {/* Live price row — refreshes every 60s during market hours.
                Shows P&L vs. the entry price so you can tell at a glance
                whether a freshly-triggered signal is working or stalling. */}
            {(() => {
              const px = pricesBySymbol?.[String(t.symbol || "").toUpperCase()];
              if (!px || px.price == null) return null;
              const live = Number(px.price);
              const delta = (t.entry != null && t.entry > 0) ? (live / t.entry - 1) : null;
              const col = delta == null ? "text-cyan-200"
                : delta >= 0 ? "text-emerald-300" : "text-red-300";
              return (
                <div className="font-mono text-[11px] flex justify-between items-center pt-1"
                     style={{ borderTop: "1px dashed rgba(59,217,255,.25)" }}>
                  <span className="opacity-70">
                    LIVE <span className={col}>${live.toFixed(2)}</span>
                    {px.stale && <span className="ml-1 opacity-50 text-[9px] uppercase tracking-widest">stale</span>}
                  </span>
                  {delta != null && (
                    <span className={col}>
                      {delta >= 0 ? "+" : ""}{(delta * 100).toFixed(2)}%
                    </span>
                  )}
                </div>
              );
            })()}
          </article>
          );
        })}
      </div>
    </Panel>
  );
}

function Radar3D({ data }) {
  const radar = data.radar || [];
  const [ts, setTs] = useState(0);
  const [hover, setHover] = useState(null);
  const [visible, setVisible] = useState(true);
  const containerRef = useRef(null);

  // Pause this RAF when the radar scrolls off-screen or the tab is hidden.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((e) => setVisible(e[0]?.isIntersecting ?? true), { rootMargin: "100px" });
    io.observe(el);
    const onVis = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => { io.disconnect(); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  // Throttle the sweep RAF to ~24fps. Re-rendering the whole radar at 60fps
  // was the single biggest cause of flicker + scroll lag.
  useEffect(() => {
    if (!visible || document.hidden) return;
    let raf, last = performance.now();
    const minDt = 1000 / 24;
    const loop = (t) => {
      if (t - last >= minDt && !document.hidden) {
        last = t;
        setTs((v) => (v + 1) % 100000);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  // Golden-angle sunflower scatter. Deterministic per symbol so the dots
  // never jitter, but spread fully across the radar disc on every browser
  // (the previous char-code hash collapsed into a visible ring on Safari
  // / Chrome-mac). We mix the symbol into a splitmix32 hash for a radial
  // offset so symbols with similar first letters don't line up.
  const positions = useMemo(() => {
    const splitmix32 = (seed) => {
      let x = 0;
      for (let i = 0; i < seed.length; i++) {
        x = ((x << 5) - x + seed.charCodeAt(i)) | 0;
      }
      x = (x ^ 0x9e3779b9) >>> 0;
      x = Math.imul(x ^ (x >>> 16), 0x85ebca6b);
      x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35);
      x = (x ^ (x >>> 16)) >>> 0;
      return x / 4294967296; // [0,1)
    };
    const GOLD = Math.PI * (3 - Math.sqrt(5));          // ~2.3999 rad
    const total = radar.length || 1;
    // Sort so open-trades sit closest to the centre (hottest signals),
    // then idle, with blocked pushed to the rim. User requested dots
    // position reflect state. We still use golden-angle for angles so
    // the pattern looks organic, not a ring.
    const stateRank = (s) => s === "in_trade" ? 0 : s === "idle" ? 1 : s === "etf" ? 2 : 3;
    const ordered = radar
      .map((b, i) => ({ b, i, rank: stateRank(b.state), jitter: splitmix32(b.symbol) }))
      .sort((a, b) => a.rank - b.rank || a.jitter - b.jitter);
    return ordered.map((row, k) => {
      // Golden-angle for the angular position
      const angle = (k * GOLD * (180 / Math.PI)) % 360;
      // Base radius from rank-sorted index gives in_trade near centre,
      // blocked near rim. Add a small per-symbol jitter so neighbours
      // don't fall on identical rings.
      const base = 55 + Math.sqrt((k + 0.5) / total) * 170;    // 55..225 px
      const r = Math.max(45, Math.min(228, base + (row.jitter - 0.5) * 24));
      return { ...row.b, angle, r, i: row.i };
    });
  }, [radar]);
  const color = (s) => s === "in_trade" ? "#5bf0b3" : s === "blocked" ? "#ffb547" : s === "etf" ? "#7fb8c9" : "#3bd9ff";
  const sweepAngle = (ts * 2.4) % 360;
  const blocked = (data.watchlist || []).filter((w) => w.blocked_today_reason);
  const openTrades = (data.watchlist || []).filter((w) => w.open_trade);

  return (
    <Panel title="Radar · Target Search" id="radar" data-testid="oracle-radar">
      <div className="grid gap-6 md:grid-cols-[minmax(0,520px)_1fr] items-start">
        {/* ----- Radar ----- */}
        <div className="relative" style={{ aspectRatio: "1 / 1", maxWidth: 520 }} ref={containerRef}>
          <div className="ora-cosmos-bg ora-cosmos-circle" />
          <svg viewBox="-260 -260 520 520" width="100%" height="100%"
               style={{ position: "relative", zIndex: 1, background: "radial-gradient(circle, rgba(59,217,255,.08), rgba(0,0,0,.25) 70%)", borderRadius: "50%" }}>
            {/* Grid rings */}
            {[60, 120, 180, 230].map((r) => (
              <circle key={r} cx="0" cy="0" r={r} fill="none" stroke="rgba(59,217,255,.18)" strokeDasharray="2 4" />
            ))}
            {/* Crosshair */}
            <line x1="-240" x2="240" y1="0" y2="0" stroke="rgba(59,217,255,.12)" />
            <line x1="0" x2="0" y1="-240" y2="240" stroke="rgba(59,217,255,.12)" />
            <defs>
              <linearGradient id="sweep" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor="rgba(59,217,255,.55)" />
                <stop offset="1" stopColor="rgba(59,217,255,0)" />
              </linearGradient>
              {/* 3-D sphere gradients, one per state colour */}
              {[
                ["sphere-cyan",  "#3bd9ff"],
                ["sphere-green", "#5bf0b3"],
                ["sphere-amber", "#ffb547"],
                ["sphere-grey",  "#7fb8c9"],
              ].map(([id, col]) => (
                <radialGradient key={id} id={id} cx="35%" cy="30%" r="65%">
                  <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.95" />
                  <stop offset="35%"  stopColor={col}     stopOpacity="1" />
                  <stop offset="100%" stopColor={col}     stopOpacity="0.55" />
                </radialGradient>
              ))}
            </defs>
            <g transform={`rotate(${sweepAngle})`}>
              <path d="M 0 0 L 240 0 A 240 240 0 0 0 185 -130 Z" fill="url(#sweep)" />
            </g>
            {/* Blips */}
            {positions.map((b) => {
              const a = (b.angle * Math.PI) / 180;
              const x = Math.cos(a) * b.r, y = Math.sin(a) * b.r;
              const c = color(b.state);
              const grad = b.state === "in_trade" ? "sphere-green" : b.state === "blocked" ? "sphere-amber" : b.state === "etf" ? "sphere-grey" : "sphere-cyan";
              const isActive = hover === b.i;
              const isOpen = b.state === "in_trade";
              // Open trades: ~1.8× the normal size and a strong attention blink.
              const size = (isOpen ? 13 : 7) + (isActive ? 2 : 0);
              return (
                <g key={b.symbol} onMouseEnter={() => setHover(b.i)} onMouseLeave={() => setHover(null)} style={{ cursor: "pointer" }}>
                  {/* soft outer glow — on hover + always for open trades */}
                  {(isActive || isOpen) && (
                    <circle cx={x} cy={y} r={size + (isOpen ? 10 : 12)}
                            fill={c} opacity={isOpen ? 0.28 : 0.22}
                            className={isOpen ? "ora-blink-strong" : undefined} />
                  )}
                  {/* 3-D sphere body */}
                  <circle cx={x} cy={y} r={size} fill={`url(#${grad})`}
                          className={isOpen ? "ora-blink-strong" : undefined} />
                  {/* specular highlight on top-left */}
                  <ellipse cx={x - size * 0.35} cy={y - size * 0.4} rx={size * 0.35} ry={size * 0.22} fill="rgba(255,255,255,0.75)" />
                  {/* Pulsing ring for open trades — larger and more visible */}
                  {isOpen && (
                    <>
                      <circle cx={x} cy={y} r={size + 5} fill="none" stroke={c} strokeWidth="1.6" className="ora-pulse" />
                      <circle cx={x} cy={y} r={size + 11} fill="none" stroke={c} strokeOpacity="0.6" strokeWidth="1" className="ora-pulse" style={{ animationDelay: ".4s" }} />
                      {/* Permanent symbol label so the hot trade is readable at a glance */}
                      <text x={x} y={y - size - 6} textAnchor="middle" fill="#e9fbff"
                            fontSize="10" fontFamily="JetBrains Mono" fontWeight="800"
                            style={{ paintOrder: "stroke", stroke: "rgba(5,14,30,.9)", strokeWidth: 2.2 }}>
                        {b.symbol}
                      </text>
                    </>
                  )}
                  {isActive && (
                    <g>
                      <rect x={x + 12} y={y - 11} width="60" height="22" rx="3"
                            fill="rgba(5,14,30,.95)" stroke={c} strokeWidth="1" />
                      <text x={x + 42} y={y + 4} textAnchor="middle" fill="#e9fbff" fontSize="11" fontFamily="JetBrains Mono" fontWeight="700">{b.symbol}</text>
                    </g>
                  )}
                </g>
              );
            })}
            {/* Centre */}
            <circle cx="0" cy="0" r="4" fill="#3bd9ff" className="ora-pulse" />
            <circle cx="0" cy="0" r="11" fill="none" stroke="rgba(59,217,255,.4)" />
          </svg>
        </div>

        {/* ----- Side list: readable, grouped by state ----- */}
        <div className="min-w-0 space-y-4">
          <div>
            <div className="ora-sub mb-2">Legend</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "#5bf0b3", boxShadow: "0 0 8px #5bf0b3" }} /> In open trade</div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "#ffb547", boxShadow: "0 0 8px #ffb547" }} /> Blocked</div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "#3bd9ff", boxShadow: "0 0 8px #3bd9ff" }} /> Idle / watching</div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "#7fb8c9" }} /> ETF (no earnings block)</div>
            </div>
          </div>

          <div>
            <div className="ora-sub mb-2">Targets · sorted by proximity</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs font-mono max-h-56 overflow-auto pr-1">
              {positions.map((b) => {
                const c = color(b.state);
                const isActive = hover === b.i;
                return (
                  <div key={b.symbol}
                       onMouseEnter={() => setHover(b.i)}
                       onMouseLeave={() => setHover(null)}
                       className={`flex items-center gap-2 cursor-pointer py-0.5 px-1 rounded ${isActive ? "bg-cyan-400/15" : ""}`}>
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: c, boxShadow: `0 0 6px ${c}` }} />
                    <span className="font-bold tracking-wider">{b.symbol}</span>
                    <span className="opacity-60 ml-auto text-[10px]">{Math.round((b.distance ?? 1) * 100)}%</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Open trades list intentionally removed from here —
              the dedicated "Open Trades · Live Log" panel below renders the
              same positions with full live P&L (price, $/%/R, stop→target
              bar, days held). Keeping it in both places created clutter. */}

          {blocked.length > 0 && (
            <div>
              <div className="ora-sub mb-2">Blocked today</div>
              <div className="text-xs font-mono space-y-0.5 max-h-32 overflow-auto">
                {blocked.map((w) => (
                  <div key={w.symbol} className="flex gap-2">
                    <span className="font-bold text-amber-300 w-14">{w.symbol}</span>
                    <span className="opacity-80">{w.blocked_today_reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
}

function NeuralMap({ data }) {
  const [sel, setSel] = useState(null);
  const [hov, setHov] = useState(null);
  const [rot, setRot] = useState({ x: 0.3, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [bob, setBob] = useState(0);
  const dragging = useRef(null);
  const autoRotateRef = useRef(true);                      // ON by default — slow idle spin
  const [autoRotate, setAutoRotate] = useState(true);      // user can pause from the panel header
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  const [visible, setVisible] = useState(true);
  const rafRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const r = () => setMobile(window.innerWidth < 600);
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);

  // Pause the RAF loop entirely when the panel scrolls off-screen or the
  // tab is hidden. Big scroll-perf win because animations stop painting.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => setVisible(entries[0]?.isIntersecting ?? true),
      { rootMargin: "100px" }
    );
    io.observe(el);
    const onVis = () => setVisible(!document.hidden && (containerRef.current ? true : false));
    document.addEventListener("visibilitychange", onVis);
    return () => { io.disconnect(); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  // Single RAF loop drives both auto-rotation and bubble bob. Fully stopped
  // when auto-rotation is OFF — no React work, zero scroll contention.
  useEffect(() => {
    if (!autoRotate || !visible || document.hidden) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }
    let last = performance.now();
    const minDt = 1000 / 30; // 30fps cap
    const tick = (t) => {
      const dt = t - last;
      if (dt >= minDt && !document.hidden) {
        last = t;
        const dts = Math.min(50, dt) / 1000;
        setRot((r) => ({ x: 0.18, y: r.y + dts * 0.55 })); // ~2x faster than before
        setBob(t / 1000);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [visible, autoRotate]);

  const nodes = useMemo(() => {
    const wl = data.watchlist || [];
    // Derive sector groupings dynamically from the LIVE watchlist so changes
    // in the admin panel (NET added, LIN/QQQM removed) show immediately on
    // the Neural Map. Any symbol not matched by the known buckets lands in
    // "Other" so the user never sees a stale hard-coded ticker.
    const KNOWN = {
      "Big Tech":  new Set(["AAPL", "NVDA", "TSLA", "NFLX", "PLTR", "AVGO"]),
      "Semis":     new Set(["AMD", "TSM", "MRVL", "AMAT", "LRCX", "SOXL", "DELL"]),
      "Defensive": new Set(["COST", "LIN", "COKE", "QQQM"]),
    };
    const sectors = { "Big Tech": [], "Semis": [], "Defensive": [], "Other": [] };
    for (const w of wl) {
      const s = String(w.symbol || "").toUpperCase();
      if (!s) continue;
      let placed = false;
      for (const [name, set] of Object.entries(KNOWN)) {
        if (set.has(s)) { sectors[name].push(s); placed = true; break; }
      }
      if (!placed) sectors["Other"].push(s);
    }
    // Drop empty groups so we never render a lonely cluster for a sector
    // the user has removed from the watchlist entirely.
    const groups = Object.entries(sectors).filter(([, syms]) => syms.length > 0);
    // Three well-separated cluster centres. We only have slots for 3 at a
    // time; if a 4th "Other" group exists we fold it into Defensive.
    const centres = [[-1.00, 0.10, 0.15], [1.00, 0.10, 0.35], [0.00, -0.75, -0.35]];
    while (groups.length > 3) {
      // merge the smallest group into the second-smallest.
      groups.sort((a, b) => a[1].length - b[1].length);
      const [, smallSyms] = groups.shift();
      groups[0][1].push(...smallSyms);
    }
    const out = [];
    let g = 0;
    for (const [name, syms] of groups) {
      syms.forEach((s, i) => {
        const w = wl.find((x) => x.symbol === s);
        const a = (i / Math.max(1, syms.length)) * Math.PI * 2 + g * 0.7;
        const [cx, cy, cz] = centres[g];
        out.push({
          sym: s, group: g, sector: name,
          x: cx + Math.cos(a) * 0.33,
          y: cy + Math.sin(a) * 0.24,
          z: cz + Math.sin(a * 2) * 0.38,
          phase: (i * 137) % 360,
          state: w?.open_trade ? "in_trade" : w?.is_etf ? "etf" : w?.blocked_today_reason ? "blocked" : "idle",
          data: w,
        });
      });
      g++;
    }
    return out;
  }, [data.watchlist]);

  // Small per-frame tick for the per-node bob so bubbles always feel alive.
  // (bob is now driven by the single RAF above)

  const project = (p, extraBob = 0) => {
    const by = Math.sin(bob * 1.1 + extraBob) * 0.02; // smaller vertical float
    const px0 = p.x, py0 = p.y + by, pz0 = p.z;
    // Y-rotation
    const cxr = Math.cos(rot.y), sxr = Math.sin(rot.y);
    const x1 = px0 * cxr - pz0 * sxr;
    const z1 = px0 * sxr + pz0 * cxr;
    // X-rotation
    const cyr = Math.cos(rot.x), syr = Math.sin(rot.x);
    const y1 = py0 * cyr - z1 * syr;
    const z2 = py0 * syr + z1 * cyr;
    const persp = 520 / (520 + z2 * 220);
    return { px: x1 * 170 * persp * zoom, py: y1 * 170 * persp * zoom, k: persp, z: z2 };
  };

  const onDown = (e) => {
    autoRotateRef.current = false;
    setAutoRotate(false);
    dragging.current = { x: e.clientX, y: e.clientY, rx: rot.x, ry: rot.y, moved: false };
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    const dx = e.clientX - dragging.current.x, dy = e.clientY - dragging.current.y;
    if (Math.abs(dx) + Math.abs(dy) > 4) dragging.current.moved = true;
    setRot({ x: dragging.current.rx + dy * 0.006, y: dragging.current.ry + dx * 0.009 });
  };
  const onUp = () => {
    dragging.current = null;
    // Do NOT auto-resume rotation on drag end. User explicitly toggles it.
  };
  const onWheel = (e) => { e.preventDefault(); setZoom((z) => Math.max(0.6, Math.min(2.2, z - e.deltaY * 0.001))); };

  const toggleAutoRotate = () => {
    const next = !autoRotateRef.current;
    autoRotateRef.current = next;
    setAutoRotate(next);
  };

  const colorHex = (s) => s === "in_trade" ? "#5bf0b3" : s === "blocked" ? "#ffb547" : s === "etf" ? "#7fb8c9" : "#3bd9ff";
  const selected = sel != null ? nodes[sel] : null;

  if (mobile) {
    return (
      <Panel title="Oracle Neural Map (list)" id="map" data-testid="oracle-neural">
        <div className="space-y-1 max-h-96 overflow-auto">
          {nodes.map((n, i) => (
            <div key={n.sym} className="flex items-center gap-2 p-2 border rounded"
                 style={{ borderColor: "rgba(59,217,255,.2)" }}
                 onClick={() => setSel(i)}>
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: colorHex(n.state), boxShadow: `0 0 8px ${colorHex(n.state)}` }} />
              <span className="font-mono font-bold">{n.sym}</span>
              <span className="ora-sub ml-auto">{n.sector}</span>
            </div>
          ))}
        </div>
        {selected && <NeuralDetail node={selected} onClose={() => setSel(null)} />}
      </Panel>
    );
  }

  return (
    <Panel title="Oracle Neural Map" id="map" data-testid="oracle-neural"
           right={<button onClick={toggleAutoRotate} className="ora-navlink" data-testid="oracle-neural-rotate">
                    {autoRotate ? "⏸ pause rotation" : "▶ auto-rotate"}
                  </button>}>
      <div className="relative mx-auto" style={{ borderRadius: 10, overflow: "hidden", maxWidth: 1100 }} ref={containerRef}>
        <div className="ora-cosmos-bg" />
        <svg
          viewBox="-350 -230 700 460" width="100%" height="460"
          preserveAspectRatio="xMidYMid meet"
          style={{
            position: "relative", zIndex: 1,
            cursor: dragging.current ? "grabbing" : "grab",
            overflow: "hidden",
            background:
              "radial-gradient(ellipse at 30% 30%, rgba(197,140,255,.05), transparent 60%)," +
              "radial-gradient(ellipse at 70% 70%, rgba(59,217,255,.07), transparent 60%)",
            borderRadius: 10,
          }}
          onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp} onWheel={onWheel}
        >
          <defs>
            {/* One radial gradient per state for 3-D bubble feel */}
            {["cyan", "amber", "green", "grey"].map((k) => {
              const [a, b] = { cyan: ["#8fecff", "#1a7fa3"], amber: ["#ffe3a7", "#a05c00"], green: ["#b7f8de", "#0f7a4a"], grey: ["#c7d6de", "#39596a"] }[k];
              return (
                <radialGradient id={`bubble-${k}`} key={k} cx="35%" cy="30%" r="70%">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
                  <stop offset="30%" stopColor={a} stopOpacity="0.95" />
                  <stop offset="100%" stopColor={b} stopOpacity="0.95" />
                </radialGradient>
              );
            })}
            <radialGradient id="halo" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.7" />
              <stop offset="60%" stopColor="currentColor" stopOpacity="0.1" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </radialGradient>
            <filter id="glow"><feGaussianBlur stdDeviation="2.4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          </defs>

          {/* Oracle core — pulsing */}
          <g>
            <circle cx="0" cy="0" r="46" fill="rgba(59,217,255,.08)" />
            <circle cx="0" cy="0" r="38" fill="rgba(59,217,255,.12)" stroke="#3bd9ff" strokeWidth="1" />
            <circle cx="0" cy="0" r="30" fill="rgba(59,217,255,.18)" className="ora-pulse" />
            <text x="0" y="4" fill="#e9fbff" fontSize="12" textAnchor="middle" fontFamily="Orbitron" fontWeight="800" letterSpacing="3">ORACLE</text>
          </g>

          {/* Links — only within sector */}
          {nodes.map((a, i) => nodes.slice(i + 1).map((b, j) => {
            if (a.group !== b.group) return null;
            const pa = project(a, a.phase), pb = project(b, b.phase);
            const linkCol = a.group === 0 ? "#3bd9ff" : a.group === 1 ? "#c58cff" : "#ffb547";
            const dim = sel != null && sel !== i && sel !== i + j + 1 ? 0.08 : 0.22 + Math.min(pa.k, pb.k) * 0.15;
            return (
              <line key={`${i}-${i + j + 1}`}
                    x1={pa.px} y1={pa.py} x2={pb.px} y2={pb.py}
                    stroke={linkCol} strokeOpacity={dim}
                    strokeWidth={0.8 + Math.min(pa.k, pb.k) * 0.6} />
            );
          }))}

          {/* Nodes — far nodes draw first, near on top; HOVERED always last so it's on top */}
          {nodes.map((n, i) => ({ n, i, p: project(n, n.phase) }))
            .sort((a, b) => {
              if (hov === a.i) return 1;      // hovered → render last
              if (hov === b.i) return -1;
              return a.p.z - b.p.z;
            })
            .map(({ n, i, p }) => {
            const key = n.state === "in_trade" ? "green" : n.state === "blocked" ? "amber" : n.state === "etf" ? "grey" : "cyan";
            const col = colorHex(n.state);
            const isSel = sel === i;
            const isHov = hov === i;
            const base = 10 * p.k;
            // Hover grows the bubble a lot; selection also grows but less.
            const r = base + (isHov ? 9 : 0) + (isSel ? 4 : 0);
            // Depth cue: fade when far (behind) + slight blur via opacity
            const depthAlpha = isHov ? 1 : (0.55 + p.k * 0.45);
            const haloOpacity = isHov ? 1.0 : isSel ? 0.9 : 0.5 * depthAlpha;
            return (
              <g key={n.sym}
                 onMouseEnter={() => setHov(i)}
                 onMouseLeave={() => setHov((h) => (h === i ? null : h))}
                 onClick={(e) => { e.stopPropagation(); if (!dragging.current || !dragging.current.moved) setSel(i); }}
                 style={{ cursor: "pointer" }}>
                {/* Big outer glow bloom on hover */}
                {isHov && (
                  <circle cx={p.px} cy={p.py} r={r * 3.6} fill="url(#halo)" style={{ color: col }} opacity="0.75" />
                )}
                {/* Outer halo */}
                <circle cx={p.px} cy={p.py} r={r * 2.6} fill="url(#halo)" style={{ color: col }} opacity={haloOpacity} />
                {/* Hover ring — spinning dashed ring */}
                {isHov && (
                  <>
                    <circle cx={p.px} cy={p.py} r={r + 7} fill="none" stroke={col} strokeWidth="1.4" strokeDasharray="3 4" opacity="0.9" className="ora-spin" style={{ transformOrigin: `${p.px}px ${p.py}px` }} />
                    <circle cx={p.px} cy={p.py} r={r + 13} fill="none" stroke={col} strokeWidth="0.8" opacity="0.5" />
                  </>
                )}
                {/* Selected ring (static dashed) */}
                {isSel && !isHov && <circle cx={p.px} cy={p.py} r={r + 10} fill="none" stroke={col} strokeDasharray="4 3" strokeWidth="1" opacity="0.7" />}
                {/* 3-D bubble body — filter only on hovered node (expensive) */}
                <circle cx={p.px} cy={p.py} r={r} fill={`url(#bubble-${key})`} opacity={depthAlpha} filter={isHov ? "url(#glow)" : undefined} />
                {/* Specular highlight */}
                <ellipse cx={p.px - r * 0.35} cy={p.py - r * 0.4} rx={r * 0.35} ry={r * 0.22} fill="rgba(255,255,255,0.85)" opacity={(isHov ? 1 : 0.7) * depthAlpha} />
                {/* Pulse ring for open trades */}
                {n.state === "in_trade" && <circle cx={p.px} cy={p.py} r={r + 4} fill="none" stroke={col} strokeWidth="1" className="ora-pulse" />}
                {/* Label */}
                <text x={p.px} y={p.py - r - 6}
                      fill="#e9fbff" fontSize={(isHov ? 13 : 10) + p.k * 2}
                      textAnchor="middle" fontFamily="JetBrains Mono"
                      fontWeight={isHov || isSel ? 800 : 500}
                      opacity={isHov ? 1 : 0.5 + p.k * 0.5}
                      style={{ paintOrder: "stroke", stroke: "rgba(5,14,30,.9)", strokeWidth: 2.4 }}>
                  {n.sym}
                </text>
              </g>
            );
          })}
        </svg>

        {selected && (
          <div className="absolute top-2 right-2 w-72 max-w-[calc(100%-16px)] ora-panel p-3 text-xs" style={{ zIndex: 10 }}>
            <NeuralDetail node={selected} onClose={() => setSel(null)} />
          </div>
        )}
      </div>
      <div className="ora-sub mt-2">drag to rotate · scroll to zoom · click node for details · auto-rotates by default</div>
    </Panel>
  );
}

function NeuralDetail({ node, onClose }) {
  const d = node.data || {};
  return (
    <div>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="ora-h" style={{ fontSize: 20 }}>{node.sym}</div>
          <div className="ora-sub">{node.sector}</div>
        </div>
        <button onClick={onClose} className="opacity-70 hover:opacity-100"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-2 gap-2 font-mono text-[11px]">
        <div><div className="opacity-70">PRICE</div>{fmt(d.last_price || d.prev_close)}</div>
        <div><div className="opacity-70">CHG</div>{d.change_pct != null ? `${d.change_pct >= 0 ? "+" : ""}${d.change_pct}%` : "—"}</div>
        <div><div className="opacity-70">SMA200</div>{d.above_sma ? <span className="text-green-400">ABOVE</span> : <span className="text-red-400">BELOW</span>}</div>
        <div><div className="opacity-70">NEXT EARN</div>{d.next_earnings ? `${d.next_earnings.date} ${d.next_earnings.hour}` : "—"}</div>
      </div>
      {d.open_trade && (
        <div className="mt-3 p-2 border border-amber-400/40 rounded text-[11px]">
          <div className="ora-sub mb-1">OPEN TRADE · SETUP {d.open_trade.setup}</div>
          <div className="font-mono flex justify-between"><span>buy {fmt(d.open_trade.entry)}</span><span>stop {fmt(d.open_trade.stop)}</span><span>tgt {fmt(d.open_trade.target)}</span></div>
        </div>
      )}
      <div className="mt-3 ora-sub">Period stats</div>
      <div className="grid grid-cols-3 gap-1 font-mono text-[11px]">
        <div><div className="opacity-70">TRADES</div>{d.period_stats?.trades ?? 0}</div>
        <div><div className="opacity-70">WIN %</div>{fmtPct(d.period_stats?.win_rate)}</div>
        <div><div className="opacity-70">TOTAL R</div>{fmtR(d.period_stats?.total_r)}</div>
      </div>
      {(d.last_trades || []).length > 0 && (
        <div className="mt-2">
          <div className="ora-sub">Last 5 trades</div>
          <div className="font-mono text-[11px] space-y-0.5">
            {d.last_trades.map((t, i) => (
              <div key={i} className="flex justify-between">
                <span>{t.entry_date} S{t.setup}</span>
                <span className={t.r >= 0 ? "text-green-400" : "text-red-400"}>{fmtR(t.r)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Reactor({ data }) {
  const r = data.reactor || {};
  // Per-setup rows. Values above 1× account are expected ("every signal
  // taken, no cash cap") — NEVER clip the capital-in-use bar at 100%.
  const SetupRow = ({ k, label }) => {
    const s = r[k] || {};
    const cap  = s.capital_in_use || 0;
    const xMul = s.capital_in_use_x != null ? s.capital_in_use_x : (r.account_size ? cap / r.account_size : 0);
    const risk = s.risk_at_stake || 0;
    const pnlD = s.daily_pnl_dollars || 0;
    const pnlR = s.daily_pnl_r || 0;
    const barPct = Math.max(0, xMul) * 100; // uncapped — bar can exceed 100%
    const pnlCol = pnlD >= 0 ? "text-green-400" : "text-red-400";
    return (
      <div className="rounded border p-3 flex flex-col gap-2"
           style={{ borderColor: "rgba(59,217,255,.3)", background: "rgba(5,14,30,.55)" }}
           data-testid={`reactor-${k}`}>
        <div className="flex items-center justify-between">
          <span className="ora-h" style={{ fontSize: 11 }}>{label}</span>
          <span className="text-[10px] opacity-60 font-mono">{s.open_positions || 0} open</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div>
            <div className="ora-sub">CAPITAL IN USE</div>
            <div className="font-mono text-sm">${fmt(cap)}</div>
            <div className="text-[10px] opacity-70">{xMul.toFixed(2)}× of ${fmt(r.account_size)}</div>
          </div>
          <div>
            <div className="ora-sub">RISK AT STAKE</div>
            <div className="font-mono text-sm">${fmt(risk)}</div>
            <div className="text-[10px] opacity-70">shares × (buy − stop)</div>
          </div>
          <div>
            <div className="ora-sub">DAILY P/L</div>
            <div className={`font-mono text-sm ${pnlCol}`}>{fmtSignedMoney(pnlD)}</div>
            <div className={`text-[10px] ${pnlCol} opacity-80`}>{fmtR(pnlR)}</div>
          </div>
        </div>
        {/* Uncapped capital-in-use bar. Marker at 1× = account size. */}
        <div className="relative mt-1">
          <div className="w-full h-1.5 rounded overflow-hidden" style={{ background: "rgba(59,217,255,.12)" }}>
            <div className="h-full" style={{
              width: `${Math.min(500, barPct)}%`,
              background: xMul > 1 ? "#ffb547" : "#3bd9ff",
              transition: "width .3s ease",
            }} />
          </div>
          {/* 1× = account size reference tick */}
          <div className="absolute top-0 bottom-0" style={{
            left: `calc(${Math.min(100, barPct)}% - 0px)`, width: 1, background: "rgba(207,234,255,.45)",
          }} />
        </div>
      </div>
    );
  };
  return (
    <Panel title="Reactor · Limits" id="reactor" data-testid="oracle-reactor">
      <div className="grid gap-3 md:grid-cols-2">
        <SetupRow k="setup_5" label="SETUP 5" />
        <SetupRow k="setup_6" label="SETUP 6" />
      </div>
      <div className="mt-3 text-[11px] opacity-70 font-mono">
        Totals · {r.open_positions || 0} open · risk ${fmt(r.risk_in_use_dollars)} · daily P/L {fmtSignedMoney(r.daily_pnl_dollars)} ({fmtR(r.daily_pnl_r)}) · period {fmtR(r.period_pnl_r)}
      </div>
    </Panel>
  );
}

function Watchlist({ data }) {
  const rows = data.watchlist || [];
  return (
    <Panel title={`Watchlist · ${rows.length} Stock${rows.length === 1 ? "" : "s"}`} id="watchlist" data-testid="oracle-watchlist">
      <div className="overflow-x-auto">
        <table className="ora-table">
          <thead><tr>
            <th>Symbol</th><th>Price</th><th>Chg%</th><th>SMA200</th><th>Earnings</th><th>Blocked today</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.symbol} data-testid={`oracle-wl-row-${r.symbol}`}>
                <td className="font-bold">{r.symbol} {r.is_etf && <span className="ora-chip ora-chip-grey">ETF</span>}</td>
                <td>{fmt(r.last_price || r.prev_close)}</td>
                <td className={r.change_pct > 0 ? "text-green-400" : r.change_pct < 0 ? "text-red-400" : ""}>{r.change_pct != null ? `${r.change_pct > 0 ? "+" : ""}${r.change_pct}%` : "—"}</td>
                <td>{r.above_sma ? <span className="text-green-400">↑</span> : <span className="text-red-400">↓</span>}</td>
                <td>{r.next_earnings ? `${r.next_earnings.date} ${r.next_earnings.hour}` : "—"}</td>
                <td className="text-amber-300">{r.blocked_today_reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function OpenTrades({ data, enriched, totals, lastUpdate, isMarketOpen, session }) {
  const rows = enriched || data.open_trades || [];
  const hasAny = rows.length > 0;
  // Thin summary line (per setup) + overall totals.
  const SetupSummary = ({ k, label }) => {
    const t = totals?.[k] || { pnl: 0, cost: 0, count: 0 };
    if (!t.count) return null;
    const col = t.pnl >= 0 ? "#45f0a6" : "#ff5c7a";
    return (
      <div className="flex items-center gap-3 text-[12px]">
        <span className="ora-h" style={{ fontSize: 10, letterSpacing: ".2em" }}>{label}</span>
        <span className="font-mono" style={{ color: col }}>
          Open P&amp;L: {fmtSignedMoney(t.pnl)}
        </span>
        <span className="opacity-70">·</span>
        <span className="font-mono opacity-80">
          {t.count} {t.count === 1 ? "position" : "positions"}
        </span>
        <span className="opacity-70">·</span>
        <span className="font-mono opacity-80">
          Money in use: {fmtMoney(t.cost)}
        </span>
      </div>
    );
  };

  return (
    <Panel title="Open Trades (Live Log)" id="open" data-testid="oracle-open">
      {!hasAny ? (
        <div className="ora-sub">No open positions.</div>
      ) : (
        <>
          <div className="flex flex-col gap-1 mb-3 pb-2" style={{ borderBottom: "1px solid rgba(59,217,255,.2)" }}>
            <SetupSummary k="s5" label="SETUP 5" />
            <SetupSummary k="s6" label="SETUP 6" />
            {lastUpdate && (
              <div className="text-[10px] opacity-70 font-mono flex items-center gap-2 flex-wrap">
                <span>
                  Live prices updated {_fmtClockUK(lastUpdate.toISOString())} UK · {isMarketOpen ? "refreshing every minute" : "market closed"}
                </span>
                <SessionChip session={session} />
                <button
                  type="button"
                  data-testid="orb-refresh-prices-btn"
                  onClick={() => {
                    window.dispatchEvent(new Event("orb:refresh-prices"));
                    toast.success("Refreshing live prices…");
                  }}
                  className="rounded border px-2 py-[2px] uppercase tracking-widest hover:bg-cyan-400/10"
                  style={{ borderColor: "rgba(59,217,255,.45)", color: "#9fe7ff" }}
                  title="Force-fetch latest Alpaca prices now (bypasses 60s cache)"
                >
                  ↻ Refresh prices
                </button>
              </div>
            )}
          </div>
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((t, i) => <LiveTradeCard key={t._id || i} t={t} />)}
          </div>
        </>
      )}
    </Panel>
  );
}


/** Compact open-trade row for the Radar panel. Shows symbol, setup, last
 * price, P&L $/R, days held/max, and a tiny stop→target bar. All values
 * come from the live-prices hook (display only). */
function RadarOpenRow({ t }) {
  const maxHold = t.setup === 6 ? 15 : 5;
  const hasPrice = t.last_price != null;
  const hasPnl = t.pnl_dollars != null;
  const col = hasPnl ? (t.pnl_dollars >= 0 ? "#45f0a6" : "#ff5c7a") : "#8ea6c6";
  const stale = !!t.stale;
  return (
    <div className="rounded border p-2 text-[11px] font-mono"
         style={{ borderColor: `${col}44`, background: "rgba(5,14,30,.55)" }}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="flex items-center gap-1.5">
          <span className="font-bold" style={{ color: "#e9fbff" }}>{t.symbol}</span>
          <span className="opacity-60 text-[9px]">S{t.setup}</span>
        </span>
        <span className="flex items-center gap-1.5" style={{ color: hasPrice ? "#e9fbff" : "#8ea6c6" }}>
          {hasPrice ? `$${t.last_price.toFixed(2)}` : "—"}
          {stale && <span className="text-[8px] uppercase opacity-60" style={{ letterSpacing: ".2em" }}>stale</span>}
        </span>
      </div>
      <StopTargetBar entry={t.entry} stop={t.stop} target={t.target} pos={t.bar_pos} hasPrice={hasPrice} />
      <div className="flex items-center justify-between mt-3 text-[10px]">
        {hasPnl ? (
          <>
            <span style={{ color: col }}>{t.pnl_dollars >= 0 ? "+" : "-"}${Math.abs(Math.round(t.pnl_dollars))}</span>
            <span style={{ color: col }}>{t.pnl_pct >= 0 ? "+" : ""}{(t.pnl_pct * 100).toFixed(1)}%</span>
            <span style={{ color: col }}>{t.pnl_r >= 0 ? "+" : ""}{t.pnl_r.toFixed(2)}R</span>
          </>
        ) : hasPrice ? (
          <span className="opacity-50">live · no P&amp;L</span>
        ) : (
          <span className="opacity-50">waiting price…</span>
        )}
        <span className="opacity-70">D {t.days_held ?? 0}/{maxHold}</span>
      </div>
    </div>
  );
}

/** One open-position card with live price + stop-to-target bar. */
function LiveTradeCard({ t }) {
  const maxHold = t.setup === 6 ? 15 : 5;
  const hasPrice = t.last_price != null;
  const hasPnl = t.pnl_dollars != null;
  const col = hasPnl ? (t.pnl_dollars >= 0 ? "#45f0a6" : "#ff5c7a") : "#8ea6c6";
  const stale = !!t.stale;
  return (
    <article className="rounded-lg border p-3 flex flex-col gap-2"
             data-testid={`live-trade-${t.symbol}-s${t.setup}`}
             style={{ borderColor: `${col}66`, background: "rgba(5,14,30,.65)" }}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <span className="ora-h" style={{ fontSize: 16 }}>{t.symbol}</span>
          <span className="text-[10px] opacity-70">S{t.setup}</span>
          {t.earnings_warning && <AlertTriangle size={11} className="text-amber-400" />}
        </div>
        <div className="text-right font-mono">
          <div style={{ fontSize: 13, color: hasPrice ? "#e9fbff" : "#8ea6c6" }}>
            {hasPrice ? fmt(t.last_price) : "—"}
            {stale && <span className="ml-1 text-[9px] uppercase opacity-60" style={{ letterSpacing: ".2em" }}>stale</span>}
          </div>
          <div className="text-[10px] opacity-60 flex items-center justify-end gap-1">
            {t.last_time ? `${_fmtClockUK(t.last_time)} UK` : "waiting"}
            <PriceSourceChip source={t.last_source} />
          </div>
        </div>
      </div>

      {/* Stop → entry → target bar (keeps a 20px bottom margin so the
          end-of-bar price labels never overlap the Buy / shares / days row) */}
      <div style={{ marginBottom: 22 }}>
        <StopTargetBar entry={t.entry} stop={t.stop} target={t.target} pos={t.bar_pos} hasPrice={hasPrice} />
      </div>

      <div className="flex items-center justify-between text-[11px] font-mono">
        <span className="opacity-70">Buy {fmt(t.entry)}</span>
        <span className="opacity-70">{t.shares} sh</span>
        <span className="opacity-70">D {t.days_held ?? 0}/{maxHold}</span>
      </div>

      {hasPnl ? (
        <div className="grid grid-cols-3 gap-1 text-[11px] font-mono">
          <div className="rounded px-2 py-1" style={{ background: `${col}14`, border: `1px solid ${col}44` }}>
            <div className="text-[9px] opacity-60" style={{ letterSpacing: ".18em" }}>P&amp;L $</div>
            <div style={{ color: col, fontSize: 12 }}>{fmtSignedMoney(t.pnl_dollars)}</div>
          </div>
          <div className="rounded px-2 py-1" style={{ background: `${col}14`, border: `1px solid ${col}44` }}>
            <div className="text-[9px] opacity-60" style={{ letterSpacing: ".18em" }}>P&amp;L %</div>
            <div style={{ color: col, fontSize: 12 }}>
              {t.pnl_pct >= 0 ? "+" : ""}{(t.pnl_pct * 100).toFixed(2)}%
            </div>
          </div>
          <div className="rounded px-2 py-1" style={{ background: `${col}14`, border: `1px solid ${col}44` }}>
            <div className="text-[9px] opacity-60" style={{ letterSpacing: ".18em" }}>P&amp;L R</div>
            <div style={{ color: col, fontSize: 12 }}>
              {t.pnl_r >= 0 ? "+" : ""}{t.pnl_r.toFixed(2)}R
            </div>
          </div>
        </div>
      ) : hasPrice ? (
        <div className="text-[10px] opacity-60 italic">Live price shown · P&amp;L unavailable (missing entry / shares)</div>
      ) : (
        <div className="text-[10px] opacity-50 italic">Waiting for live price…</div>
      )}
    </article>
  );
}

function StopTargetBar({ entry, stop, target, pos, hasPrice }) {
  const entryPos = (target === stop) ? 0.5 : Math.max(0, Math.min(1, (entry - stop) / (target - stop)));
  const safePos = (pos == null || Number.isNaN(pos)) ? entryPos : Math.max(0, Math.min(1, pos));
  return (
    <div className="relative" style={{ height: 14 }}>
      {/* Base bar */}
      <div className="absolute inset-y-0 inset-x-0 rounded-full" style={{
        background: "linear-gradient(90deg, rgba(255,92,122,.28), rgba(59,217,255,.14) 50%, rgba(69,240,166,.28))",
        border: "1px solid rgba(59,217,255,.25)",
      }} />
      {/* Entry tick */}
      <div className="absolute top-0 bottom-0" style={{
        left: `calc(${entryPos * 100}% - 1px)`, width: 2,
        background: "rgba(207,234,255,.65)",
      }} />
      {/* Price marker */}
      {hasPrice && (
        <div className="absolute top-1/2 -translate-y-1/2 rounded-full" style={{
          left: `calc(${safePos * 100}% - 6px)`,
          width: 12, height: 12,
          background: "#e9fbff",
          boxShadow: "0 0 6px rgba(233,251,255,.7)",
          border: "1px solid rgba(59,217,255,.7)",
        }} />
      )}
      {/* End labels */}
      <div className="absolute -bottom-4 left-0 text-[9px] font-mono text-red-300 opacity-80">{fmt(stop)}</div>
      <div className="absolute -bottom-4 right-0 text-[9px] font-mono text-emerald-300 opacity-80">{fmt(target)}</div>
    </div>
  );
}

function Results({ data }) {
  const s5 = data.results?.setup_5 || {};
  const s6 = data.results?.setup_6 || {};
  const Row = ({ label, s }) => (
    <tr><td className="font-bold">{label}</td>
      <td>{s.trades || 0}</td>
      <td>{s.wins || 0}/{s.losses || 0}</td>
      <td>{fmtPct(s.win_rate)}</td>
      <td className={s.total_r >= 0 ? "text-green-400" : "text-red-400"}>{fmtR(s.total_r)}</td>
      <td>{fmtR(s.avg_r)}</td>
      <td>{s.profit_factor ?? "∞"}</td>
      <td className="text-red-300">{fmtR(s.max_drawdown_r)}</td>
    </tr>
  );
  // Simple equity-curve SVG overlaying both setups
  const curves = [{ k: "setup_5", color: "#3bd9ff", data: s5.equity_curve || [] }, { k: "setup_6", color: "#ffb547", data: s6.equity_curve || [] }];
  const all = curves.flatMap(c => c.data.map(p => p.cum_r));
  const maxR = Math.max(1, ...all), minR = Math.min(0, ...all);
  const scaleY = (r) => 100 - ((r - minR) / (maxR - minR || 1)) * 80;
  const scaleX = (i, n) => (i / Math.max(1, n - 1)) * 100;
  return (
    <Panel title="Results · Setup 5 vs Setup 6" id="results" data-testid="oracle-results">
      <div className="overflow-x-auto mb-3">
        <table className="ora-table">
          <thead><tr><th>Setup</th><th>Trades</th><th>W/L</th><th>Win%</th><th>Total R</th><th>Avg R</th><th>PF</th><th>Max DD</th></tr></thead>
          <tbody><Row label="SETUP 5" s={s5} /><Row label="SETUP 6" s={s6} /></tbody>
        </table>
      </div>
      <div className="ora-sub mb-1">Equity curves (cumulative R)</div>
      <svg viewBox="0 0 100 100" width="100%" height="200" preserveAspectRatio="none" style={{ background: "rgba(5,14,30,.4)", border: "1px solid rgba(59,217,255,.2)", borderRadius: 6 }}>
        <line x1="0" x2="100" y1={scaleY(0)} y2={scaleY(0)} stroke="rgba(59,217,255,.2)" strokeDasharray="1 1" />
        {curves.map(c => (
          <polyline key={c.k} fill="none" stroke={c.color} strokeWidth="0.4"
            points={c.data.map((p, i) => `${scaleX(i, c.data.length)},${scaleY(p.cum_r)}`).join(" ")} />
        ))}
      </svg>
      <div className="flex gap-3 text-[11px] mt-1">
        <span><span className="inline-block w-3 h-0.5 align-middle" style={{ background: "#3bd9ff" }} /> Setup 5</span>
        <span><span className="inline-block w-3 h-0.5 align-middle" style={{ background: "#ffb547" }} /> Setup 6</span>
      </div>
    </Panel>
  );
}

function ClosedTrades({ data }) {
  const rows = data.closed_trades || [];
  const [setup, setSetup] = useState("all");
  const [sym, setSym] = useState("");
  const [res, setRes] = useState("all");
  const filtered = rows.filter(t =>
    (setup === "all" || String(t.setup) === setup) &&
    (!sym || (t.symbol || "").toUpperCase().includes(sym.toUpperCase())) &&
    (res === "all" || (res === "win" ? (t.r || 0) > 0 : (t.r || 0) <= 0))
  );
  const exportCsv = () => {
    const head = ["symbol", "setup", "entry_date", "exit_date", "entry", "stop", "target", "exit_price", "r", "shares", "exit_reason", "earnings_warning"];
    const csv = [head.join(",")].concat(filtered.map(t => head.map(k => JSON.stringify(t[k] ?? "")).join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `oracle-trades-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };
  return (
    <Panel title="Mission Log · Closed Trades" id="history" data-testid="oracle-history"
      right={
        <div className="flex flex-wrap gap-2 items-center">
          <Input placeholder="Symbol" value={sym} onChange={e => setSym(e.target.value)} className="h-8 w-24" />
          <Select value={setup} onValueChange={setSetup}>
            <SelectTrigger className="h-8 w-24"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All setups</SelectItem><SelectItem value="5">Setup 5</SelectItem><SelectItem value="6">Setup 6</SelectItem></SelectContent>
          </Select>
          <Select value={res} onValueChange={setRes}>
            <SelectTrigger className="h-8 w-24"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="win">Wins</SelectItem><SelectItem value="loss">Losses</SelectItem></SelectContent>
          </Select>
          <Button size="sm" onClick={exportCsv} data-testid="oracle-export-csv">CSV</Button>
        </div>
      }
    >
      <div className="overflow-x-auto">
        <table className="ora-table">
          <thead><tr><th>Sym</th><th>S</th><th>Entered</th><th>Exited</th><th>Entry</th><th>Exit</th><th>R</th><th>Reason</th><th>⚠</th></tr></thead>
          <tbody>
            {filtered.slice(0, 500).map((t, i) => (
              <tr key={i}>
                <td className="font-bold">{t.symbol}</td>
                <td>S{t.setup}</td>
                <td>{t.entry_date}</td>
                <td>{t.exit_date || "—"}</td>
                <td>{fmt(t.entry)}</td>
                <td>{fmt(t.exit_price)}</td>
                <td className={t.r >= 0 ? "text-green-400" : "text-red-400"}>{fmtR(t.r)}</td>
                <td className="text-[11px] opacity-80">{t.exit_reason || "—"}</td>
                <td>{t.earnings_warning && <AlertTriangle size={12} className="text-amber-400" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="ora-sub mt-2">{filtered.length} trades (showing first 500)</div>
    </Panel>
  );
}

// --- Main page ---
export default function OracleTradesPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const rootRef = useRef(null);
  const [paused, setPaused] = useState(false);

  const load = async () => {
    try {
      const d = await apiGet("/orb/dashboard");
      setData(d); setErr("");
    } catch (e) { setErr(e.message || "Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh: 30s during market hours, 5min otherwise. Pause when hidden.
  // Also toggle a body class so CSS can pause every background animation
  // while the tab is in the background.
  useEffect(() => {
    const onVis = () => document.body.classList.toggle("ora-tab-hidden", document.hidden);
    onVis();
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); document.body.classList.remove("ora-tab-hidden"); };
  }, []);

  useEffect(() => {
    let timer;
    const schedule = () => {
      if (document.hidden) { setPaused(true); return; }
      setPaused(false);
      const interval = data?.market?.is_open ? 30000 : 300000;
      timer = setTimeout(async () => { await load(); schedule(); }, interval);
    };
    schedule();
    const onVis = () => { clearTimeout(timer); schedule(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearTimeout(timer); document.removeEventListener("visibilitychange", onVis); };
  }, [data?.market?.is_open]);

  // Perf-lite mode detector. Any desktop/laptop viewport (>= 1100px wide)
  // runs in perf-lite — this strips the animated star-fields and comets
  // on the heavy Oracle panels. User reports Mac/PC scrolling and radar
  // rendering break down with those layers active; iPhone/iPad (< 1100px)
  // render smoothly, so we keep the full visuals there.
  useEffect(() => {
    const detect = () => {
      const w = typeof window !== "undefined" ? window.innerWidth : 0;
      // iPad landscape is 1024px, iPhone max ~430px → threshold 1100 keeps
      // them in full-visuals mode while every real laptop (>=1280 Mac /
      // 1366+ PC) drops into perf-lite.
      const on = w >= 1100;
      document.body.classList.toggle("ora-perf-lite", on);
    };
    detect();
    window.addEventListener("resize", detect);
    document.addEventListener("fullscreenchange", detect);
    document.addEventListener("webkitfullscreenchange", detect);
    return () => {
      window.removeEventListener("resize", detect);
      document.removeEventListener("fullscreenchange", detect);
      document.removeEventListener("webkitfullscreenchange", detect);
      document.body.classList.remove("ora-perf-lite");
    };
  }, []);

  if (loading && !data) return <div className="p-10 text-center">Loading Oracle Trades…</div>;
  if (err && !data) return <div className="p-10 text-center text-red-400">{err}</div>;
  if (!data) return null;

  // Live P&L hook — enriches open trades with Alpaca last price, computes
  // totals per setup for the status strip.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return <OracleTradesInner data={data} rootRef={rootRef} paused={paused} onLogout={() => { logout?.(); nav("/login"); }} />;
}

function OracleTradesInner({ data, rootRef, paused, onLogout }) {
  const openTrades = data.open_trades || [];
  const isMarketOpen = !!data.market?.is_open;
  // Also pull live prices for any symbol on today's signal cards so the
  // "LIVE" row renders for freshly-triggered setups (e.g. NET) that have
  // not yet been promoted to an open position.
  const signalSymbols = useMemo(
    () => (data.todays_signals || []).map((s) => s?.symbol).filter(Boolean),
    [data.todays_signals]
  );
  const { pricesBySymbol, enriched, totals, lastUpdate, session } = useLivePrices(
    openTrades,
    { isMarketOpen, extraSymbols: signalSymbols }
  );
  const extendedHours = session === "pre" || session === "post";

  return (
    <div ref={rootRef} className={`oracle-root ${paused ? "ora-paused" : ""}`} data-testid="oracle-trades-page">
      <div className="nebula" />
      <div className="stars" />
      <div className="stars stars-b" />
      <div className="stars-c" />
      <div className="p-4 md:p-6">
        <div className="mb-3">
          <OracleNav onLogout={onLogout} />
        </div>
        <Header data={data} />
        <SessionBanner session={session} />
        <RiskStatusStrip openPnl={{ s5: totals.s5.pnl, s6: totals.s6.pnl }} />
        <TodaysSignals data={data} pricesBySymbol={pricesBySymbol} session={session} />
        <Radar3D data={data} />
        <NeuralMap data={data} />
        <Reactor data={data} />
        <Watchlist data={data} />
        <OpenTrades data={data} enriched={enriched} totals={totals} lastUpdate={lastUpdate} isMarketOpen={isMarketOpen} session={session} />
        <ClosedTradesToday />
        <Results data={data} />
        <ClosedTrades data={data} />
      </div>
    </div>
  );
}
